import type { PropsWithChildren, ReactNode } from 'react';
import { useEffect } from 'react';

import { Navigate, useLocation } from 'react-router';

import type { Endomorphism } from '@code-expert/prelude';
import { identity } from '@code-expert/prelude';
import { isProduction } from '/imports/config/client';
import type { UserCapability, UserPublic } from '/imports/domain';
import { checkCapabilities, isExamUser, isLTIUser } from '/imports/domain';
import { formatRoute } from '/imports/modules/Routing';
import { loginDemoRoute, loginRoute } from '/imports/startup/client/routes';
import { useGlobalContext } from '/imports/ui/components/GlobalContext';
import { NotAuthorized } from '/imports/ui/components/NotFound';
import { examLogoutRoute, examStudentRoute } from '/imports/ui/pages/exam/routes';
import { pushLastLocation } from '/imports/ui/pages/login/utils';
import { uiSessionStates } from '/imports/utils/UiState';
import { UserInboxContextProvider } from './Messages/UserInboxContext';

function RedirectToLogin() {
  const location = useLocation();
  pushLastLocation(location);
  if (uiSessionStates.isExamSession.get() === true) {
    uiSessionStates.isExamSession.clear();
    return <Navigate to={formatRoute(examLogoutRoute)({})} />;
  }
  return (
    <Navigate to={isProduction ? formatRoute(loginRoute)({}) : formatRoute(loginDemoRoute)({})} />
  );
}

function Unauthorized({ user }: { user: UserPublic }) {
  useEffect(() => {
    if (user != null && isLTIUser(user)) {
      void fetch('/logout', { method: 'POST' });
    }
  }, [user]);
  if (user != null) {
    if (isExamUser(user)) {
      const { examId } = user;
      return <Navigate to={formatRoute(examStudentRoute)({ examId })} />;
    }
  }
  return <NotAuthorized />;
}

// -------------------------------------------------------------------------------------------------

interface AuthorisePublicProps {
  allow?: Array<UserCapability>;
  deny?: Array<UserCapability>;
  redirectToHomepage?: true;
  // We need a dedicated layout function because the layout is also applied to the "Unauthorised" page.
  layout?: Endomorphism<ReactNode>;
  children: ReactNode | ((_: UserPublic) => ReactNode);
}

/**
 * Render the children without requiring authentication.
 * If a user is logged in, their capabilities are verified.
 */
export const AuthorisePublic = ({
  allow,
  deny,
  redirectToHomepage,
  layout = identity,
  children,
}: PropsWithChildren<AuthorisePublicProps>) => {
  const { user, userCapabilities } = useGlobalContext();
  const isAuthorized = user != null && checkCapabilities({ allow, deny }, userCapabilities);

  return user == null ? (
    layout(children)
  ) : isAuthorized ? (
    <UserInboxContextProvider>{layout(children)}</UserInboxContextProvider>
  ) : redirectToHomepage ? (
    <Navigate to="/" />
  ) : (
    <UserInboxContextProvider>{layout(<Unauthorized user={user} />)}</UserInboxContextProvider>
  );
};

export type AuthoriseProps = AuthorisePublicProps & {
  children: ReactNode | ((user: UserPublic) => ReactNode);
};

/**
 * Render the children only if a user is logged in and has the required capabilities.
 */
export const Authorise = ({
  allow,
  deny,
  redirectToHomepage,
  layout = identity,
  children,
}: AuthoriseProps) => {
  const { user, userCapabilities } = useGlobalContext();
  const isAuthorized = user != null && checkCapabilities({ allow, deny }, userCapabilities);

  return user == null ? (
    <RedirectToLogin />
  ) : isAuthorized ? (
    <UserInboxContextProvider>
      {layout(typeof children === 'function' ? children(user) : children)}
    </UserInboxContextProvider>
  ) : redirectToHomepage ? (
    <Navigate to="/" />
  ) : (
    <UserInboxContextProvider>{layout(<Unauthorized user={user} />)}</UserInboxContextProvider>
  );
};
